import { createExperimentNormalizer, createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

const elementShape = {
	type: 'object',
	optional: true,
	shape: {
		visible: { type: 'boolean', optional: true },
		value: { type: 'string', optional: true },
	},
} as const;

export const FCM_PSF_COMBO_COPY_EXPERIMENT = defineFcm('psf_combo_copy_experiment')
	.asJson(
		createShapeNormalizer({
			type: 'object',
			shape: {
				experimentName: { type: 'string' },
				variants: {
					type: 'other',
					normalizer: createExperimentNormalizer(
						['control', 'fake_control', 'variant_1', 'variant_2', 'variant_3'],
						createShapeNormalizer({
							type: 'object',
							shape: {
								headlineTextIdentified: elementShape,
								subheadingText1: elementShape,
								subheadingText2: elementShape,
								subheadingText3: elementShape,
								subheadingContributionHero: elementShape,
								promoteButtonWithGoalText: elementShape,
								shareButtonText: elementShape,
								promoterCountHeading: elementShape,
								joinTheCommunityCustomText: elementShape,
								skipLinkText: elementShape,
								guestLoginBannerNewUserVerify: elementShape,
							},
						}),
					),
				},
			},
		}),
	)
	.withDefault(undefined)
	.withMerger(({ globalValue, countryValue, localeValue }) => {
		return countryValue ?? localeValue ?? globalValue;
	})
	.toConfig({
		owner: 'squad-koi',
		scope: 'corgi-front-end',
		context: 'psf combo (promote-or-share) page',
		description: `Per-locale copy experiment config for the combo screen (CHANGE-95803 de-DE, CHANGE-95804 ja-JP).

			- experimentName: experiment service name for the locale this value is set on
			- variants: per-variation copy overrides, keyed by the page's copy slots (usePageCopy TranslationName keys)
			- each slot: { visible?: boolean, value?: string } — unset slot falls back to the default i18n copy,
			  visible: false hides the element, value replaces the translated string
			- fake_control is always rendered with the control payload by the front-end (A/A) — no need to set it
			- subheadingText3 value may contain HTML (rendered via the page's Html component)

			Replacements available in value strings:
			- preferredName: signer's short display name
			- chip_in_amount_formatted: localized chip-in amount
			- contributionCount: localized promoter count (subheadingContributionHero)
			- user_email: pending signature email (guestLoginBannerNewUserVerify only)
			- custom_promoter_count / custom_contribution_amount / custom_additional_signatures:
			  localized community stats (joinTheCommunityCustomText only)

			joinTheCommunityCustomText overrides the "join the community" footer text only when the
			custom community config is active; without it the default copy behavior is untouched.

			guestLoginBannerNewUserVerify is the "Email Verification Banner" shown to guest signers
			(fe.components.psf.guest_login_banner.new_user_verify).`,
		mergeLogic: 'Displays in order of country, locale, then global',
		template: {
			experimentName: '__invalid_experiment__',
			variants: {
				control: {
					headlineTextIdentified: { value: '%{preferredName}, current headline' },
					subheadingText2: { value: 'Current ask with %{chip_in_amount_formatted}' },
				},
				variant_1: {
					headlineTextIdentified: { value: '%{preferredName}, new headline' },
					subheadingText2: { visible: false },
					subheadingText3: { value: '<strong>HTML body</strong> for the variant' },
				},
			},
		},
	});
